import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import { UserProfileSchema } from "~/lib/schemas/user-auth";

export const getUserProfile = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(({ context }) => {
    const user = context.user;
    return {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phoneNumber: user?.phoneNumber || undefined,
      photo: user?.profilePictureUrl || undefined,
      title: user?.title || undefined,
      bio: user?.bio || undefined,
      clerkUserId: user?.clerkUserId || undefined,
    };
  });

export const updateUserProfile = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(UserProfileSchema)
  .handler(async ({ data, context }) => {
    try {
      const userId = context.user?.id;
      const clerkUserId = context.user?.clerkUserId;

      if (!userId || !clerkUserId) {
        throw new Error("User not authenticated");
      }

      const { saveUserProfile } = await import("~/lib/server/user-profile");
      await saveUserProfile({ userId, clerkUserId }, data);

      return {
        status: "success",
        message: "User updated",
      };
    } catch (err) {
      console.error("Failed to update user profile:", {
        error: err,
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
      });
      // Re-throw with a user-friendly message, preserving the original error
      // as `cause` so Sentry retains the real stack/root cause instead of
      // just this wrapper (see reportToSentry's postgres fingerprinting,
      // which reads err.cause).
      throw new Error("Failed to update user", { cause: err });
    }
  });

/**
 * Switches the caller's account to an email address they have already
 * verified in Clerk, and carries the change to the User and Coach rows and the
 * GHL contact. It takes the Clerk address id, never a typed-in email, and only
 * acts on the signed-in account. This is the only way an account's email
 * changes.
 */
export const confirmEmailChange = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ emailAddressId: z.string().min(1) }))
  .handler(async ({ data, context }) => {
    const { changeAccountEmail } = await import("~/lib/server/account-email");
    return changeAccountEmail({
      userId: context.user.id,
      clerkUserId: context.user.clerkUserId,
      emailAddressId: data.emailAddressId,
    });
  });

export const setUserPassword = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ password: z.string().min(8) }))
  .handler(async ({ data, context }) => {
    const clerkUserId = context.user?.clerkUserId;
    if (!clerkUserId) throw new Error("User not authenticated");
    const { updateClerkUserPassword } =
      await import("~/lib/server/clerk-server");
    await updateClerkUserPassword(clerkUserId, data.password);
  });

/**
 * Schedules deletion of the *calling* account. The identity is taken from the
 * authenticated session, never from the request body, so a caller structurally
 * cannot schedule deletion of an account other than their own.
 */
export const userScheduleDeletion = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { scheduleOwnAccountDeletion } =
      await import("~/lib/server/user-deletion/schedule");
    // The record is looked up by `users.clerkUserId`.
    return scheduleOwnAccountDeletion({
      userId: context.user.clerkUserId,
      userEmail: context.user.email,
    });
  });

/** When the calling account is due to be deleted; null when nothing is scheduled. */
export const getOwnDeletionSchedule = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(
    async ({ context }): Promise<{ scheduledForDeletionAt: string | null }> => {
      const { getScheduledDeletionDate } =
        await import("~/lib/server/user-deletion/schedule");
      const scheduledForDeletionAt = await getScheduledDeletionDate(
        context.user.clerkUserId,
      );
      return {
        scheduledForDeletionAt: scheduledForDeletionAt?.toISOString() ?? null,
      };
    },
  );

/**
 * Cancels the *calling* account's scheduled deletion. As with scheduling, the
 * identity comes from the session, never from the request body.
 */
export const userCancelDeletion = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { cancelUserDeletion } =
      await import("~/lib/server/user-deletion/schedule");
    return cancelUserDeletion({ userId: context.user.clerkUserId });
  });
